import {
  BarcodeOutlined,
  BgColorsOutlined,
  BuildOutlined,
  CrownOutlined,
  DatabaseOutlined,
  FieldTimeOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FormOutlined,
  InfoCircleOutlined,
  PartitionOutlined,
  SolutionOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { App, Button, Menu, Result, theme } from 'antd';
import type React from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { useParamCompilationStore } from '../../hooks/store/useParamCompilationStore';
import { COMPILE_PARAMS_MUTATION, LAB_QUERY } from './Lab.gql';
import { AnalysisStations } from './lab/AnalysisStations';
import { Basedata } from './lab/Basedata';
import { Catalogs } from './lab/Catalogs';
import { Diagnoses } from './lab/Diagnoses';
import { Documents } from './lab/Documents';
import { Forms } from './lab/Forms';
import { News } from './lab/News.tsx';
import { Parameters } from './lab/Parameters';
import { PeriodicGroups } from './lab/PeriodicGroups.tsx';
import { Sheets } from './lab/Sheets';
import { SpecialRates } from './lab/SpecialRates';
import { Specimens } from './lab/Specimens';
import { TestTubes } from './lab/TestTubes';

export const Lab: React.FC = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const { token } = theme.useToken();

  const levels = location.pathname.split('/');
  const level1 = levels[4];
  const level2 = levels[5];
  let selectedKey = level1;
  if (level2) {
    selectedKey = `${level1}/${level2}`;
  }

  const { data, loading } = useQuery(LAB_QUERY, {
    variables: {
      id: params.id ?? '',
    },
    fetchPolicy: 'cache-and-network', // use cache here to update the name when the basedata changes
  });

  const [compileParamsMutation] = useMutation(COMPILE_PARAMS_MUTATION);
  const compiling = useParamCompilationStore(s => s.status === 'compiling');
  const startCompilation = useParamCompilationStore(s => s.startCompilation);
  const dismissCompilation = useParamCompilationStore(s => s.dismiss);

  const handleBack = () => navigate('/admin/labors');

  if (!loading && data?.lab == null) {
    return (
      <Result
        status={404}
        title="Labor nicht gefunden"
        subTitle={`Das Labor mit ID ${params.id} konnte nicht gefunden werden`}
        extra={
          <Button onClick={handleBack} type="primary">
            Zurück
          </Button>
        }
      />
    );
  }

  const styles = {
    wrapper: css`
      display: flex;
      flex-direction: column;
      height: 100%;
    `,
    pageHeader: css`
      border-bottom: 1px solid ${token.colorBorderSecondary};
      background-color: white !important;
      padding: ${token.paddingMD}px ${token.paddingLG}px 0 ${token.paddingLG}px;
    `,
    content: css`
      overflow-y: auto;
      height: 100%;
    `,
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={`${data?.lab?.name} (${data?.lab?.shortName})`}
        subTitle={data?.lab?.longName}
        onBack={handleBack}
        extra={
          <Button
            icon={<BuildOutlined />}
            onClick={() => {
              startCompilation();
              compileParamsMutation().catch(() => {
                dismissCompilation();
                message.error('Fehler beim Zusammenstellen der Parameter.');
              });
            }}
            loading={compiling}
          >
            Parameter zusammenstellen
          </Button>
        }
        className={styles.pageHeader}
      >
        <Menu
          defaultSelectedKeys={['basisdaten']}
          selectedKeys={[selectedKey]}
          mode="horizontal"
          style={{ borderBottom: 0, backgroundColor: 'white' }}
          items={[
            {
              key: 'basisdaten',
              icon: <FileSearchOutlined />,
              label: <Link to="../basisdaten">Basisdaten</Link>,
            },
            {
              key: 'news',
              icon: <InfoCircleOutlined />,
              label: <Link to="../news">News</Link>,
            },
            {
              key: 'parameter',
              icon: <TableOutlined />,
              label: 'Parameter',
              children: [
                {
                  key: 'parameter/parameter',
                  icon: <TableOutlined />,
                  label: <Link to="../parameter/parameter">Alle Laborparameter</Link>,
                },
                {
                  key: 'parameter/kataloge',
                  icon: <DatabaseOutlined />,
                  label: <Link to="../parameter/kataloge">Parameter-Kataloge</Link>,
                },
                {
                  key: 'parameter/periodische-verrechnungsgruppen',
                  icon: <FieldTimeOutlined />,
                  label: <Link to="../parameter/periodische-verrechnungsgruppen">Periodische Verrechnungsgruppen</Link>,
                },
              ],
            },
            {
              key: 'material',
              icon: <BarcodeOutlined />,
              label: <Link to="../material">Material</Link>,
            },
            {
              key: 'probenbehaelter',
              icon: <BgColorsOutlined />,
              label: <Link to="../probenbehaelter">Probenbehälter</Link>,
            },
            {
              key: 'sondertarife',
              icon: <CrownOutlined />,
              label: <Link to="../sondertarife">Sondertarife</Link>,
            },
            {
              key: 'diagnosen',
              icon: <SolutionOutlined />,
              label: <Link to="../diagnosen">Diagnosen</Link>,
            },
            {
              key: 'dokumente',
              icon: <FileTextOutlined />,
              label: <Link to="../dokumente">Dokumente</Link>,
            },
            {
              key: 'formulare',
              icon: <FormOutlined />,
              label: <Link to="../formulare">Formulare</Link>,
            },
            {
              key: 'sheets',
              icon: <FileDoneOutlined />,
              label: <Link to="../sheets">Sheets</Link>,
            },
            {
              key: 'analysestationen',
              icon: <PartitionOutlined />,
              label: <Link to="../analysestationen">Analysestationen</Link>,
            },
          ]}
        />
      </PageHeader>
      <div className={styles.content}>
        <Routes>
          <Route path="basisdaten" element={<Basedata />} />
          <Route path="news" element={<News />} />
          <Route path="parameter/parameter" element={<Parameters />} />
          <Route path="parameter/kataloge" element={<Catalogs />} />
          <Route path="parameter/periodische-verrechnungsgruppen" element={<PeriodicGroups />} />
          <Route path="material" element={<Specimens />} />
          <Route path="probenbehaelter" element={<TestTubes />} />
          <Route path="sondertarife" element={<SpecialRates />} />
          <Route path="diagnosen" element={<Diagnoses />} />
          <Route path="dokumente" element={<Documents />} />
          <Route path="formulare" element={<Forms />} />
          <Route path="sheets" element={<Sheets />} />
          <Route path="analysestationen" element={<AnalysisStations />} />
          <Route path="*" element={<Navigate replace to="../basisdaten" />} />
        </Routes>
      </div>
    </div>
  );
};
